<script setup lang="ts">
import {legacyModeKey, useHost, useLocalization, useProcesses} from '@hantera/portal-app';
import {Spinner, TextInput} from '@hantera/design-system';
import {computed, inject, nextTick, onMounted, onUnmounted, reactive, ref, useTemplateRef, watch} from "vue";
import Decimal from "decimal.js";
import useBackend from "@/composition/useBackend";
import {v7} from "uuid";
import {Pane, TabView} from "@/view-managers/workspace/workspace-types";
import useWorkspace from "@/composition/useWorkspace";
import {SearchResult, SearchResultOrder, SearchResultQuery} from "@/components/QuickSearch/search-types";
import ResultOrder from "@/components/QuickSearch/ResultOrder.vue";

const t = useLocalization();
const { queries, graph } = useBackend();
const workspace = useWorkspace();
const host = useHost();
const processes = useProcesses()

const legacyMode = inject(legacyModeKey, false)

const phrase = ref('');

const results: SearchResult[] = reactive([]);
const hasFocus = ref(false);
const isLoading = ref(false);
const selected = ref(-1);

const containerElem = useTemplateRef<HTMLDivElement>('containerElem');
const resultsElem = useTemplateRef<HTMLDivElement>('resultsElem');
const textInput = useTemplateRef('textInput');
const resultsStyle = ref({});
let debouncer: ReturnType<typeof setTimeout> | undefined;
let abortController: AbortController | undefined;

const displayResults = computed(() => hasFocus.value && phrase.value.length >= 3);

watch(phrase, () => {
  if (debouncer) {
    clearTimeout(debouncer);
  }

  if (phrase.value.trim().length >= 3) {
    debouncer = setTimeout(performSearch, 400);
  } else {
    results.splice(0, results.length);
  }
});

watch(displayResults, updateResultsStyle);
watch(results, updateResultsStyle);

function updateResultsStyle() {

  resultsStyle.value = {};
  nextTick(() => {
    if (!containerElem.value || !resultsElem.value) {
      return;
    }
    
    const style = {} as any;
    
    const rect = containerElem.value.getBoundingClientRect();
    const topSpace = rect.top;
    const bottomSpace = window.innerHeight - rect.top - rect.height;

    const resultHeight = resultsElem.value.scrollHeight + 2; // +2 for the border
    let availableHeight = bottomSpace;
    
    if (resultHeight > bottomSpace && resultHeight < topSpace) {
      style.bottom = (window.innerHeight - rect.top) + 'px';
      availableHeight = topSpace;
    } else {
      style.top = (rect.top + rect.height) + 'px';
    }
    style.left = rect.left + 'px';

    if (availableHeight < resultHeight) {
      style.overflowY = 'scroll';
    } else {
      style.overflowY = 'hidden';
    }
    
    if (displayResults.value) {
      style.maxHeight = Math.min(availableHeight, resultHeight) + 'px';
    } else {
      style.maxHeight = '0';
      style.border = '0 solid transparent';
    }

    const inputWidth = containerElem.value.getBoundingClientRect().width;
    const resultWidth = resultsElem.value.scrollWidth;
    const availableWidth = window.innerWidth;

    const actualWidth = Math.min(availableWidth, Math.max(inputWidth, resultWidth));

    if (rect.left + actualWidth > availableWidth) {
      style.left = availableWidth - rect.left - actualWidth + 'px';
    }

    style.width = Math.min(availableWidth, Math.max(inputWidth, resultWidth)) + 'px';
    
    resultsStyle.value = style;
  });
  
}

function closeResults() {
  textInput.value?.blur();
  hasFocus.value = false;
  selected.value = -1;
}

onMounted(() => {
  document.addEventListener('click', closeResults);
  document.addEventListener('resize', updateResultsStyle);
  document.addEventListener('scroll', updateResultsStyle);
});
onUnmounted(() => {
  document.removeEventListener('click', closeResults);
  document.removeEventListener('resize', updateResultsStyle);
  document.removeEventListener('scroll', updateResultsStyle);
});


async function performSearch() {
  
  if (abortController) {
    abortController.abort();
  }

  abortController = new AbortController();
  
  results.splice(0, results.length);
  isLoading.value = true;
  
  try
  {
    const querySearchWords = phrase.value.toLowerCase().trim().split(' ');
    
    const queryHits = Object.values(queries.queries).filter(q => {
      return querySearchWords.find(w => q.name.toLowerCase().indexOf(w) !== -1) !== undefined
    }).map(q => ({
      type: 'query',
      data: {
        id: q.id,
        name: q.name
      },
      onClick: () => {

        let pane: Pane | undefined;

        if (workspace.state.activePaneId) {
          pane = workspace.state.panes.find(r => r.paneId === workspace.state.activePaneId);
        }
        if (!pane) {
          pane = workspace.state.panes[0];
        }

        if (!pane) { return; }

        const existing = pane.tabs.find(r => (r as TabView).path === '/query' && (r as TabView).query?.id === q.id);

        if (existing) {
          pane.selectedTabId = existing.tabId;
          return;
        }
        
        workspace.openTab(pane.paneId, {
          type: 'view',
          tabId: v7(),
          path: '/query',
          query: {
            id: q.id,
          },
          count: 0,
          state: {
            showEditor: false,
            selections: {
              filters: {},
            }
          }
        } as TabView);
        
      }
    } as SearchResultQuery));
    
    results.splice(0, 0, ...queryHits);
    
    const response = (await graph.queryGraph<{
      orderId: string
      orderNumber: string
      orderState: string
      orderTotal: Decimal
      currencyCode: string
      channelKey: string
      tags: string[]
      createdAt: Date
    }>({
      edge: 'orders',
      phrase: phrase.value,
      orderBy: 'createdAt desc',
      limit: 15,
      node: {
        fields: ['orderId', 'orderNumber', 'orderState', 'createdAt', 'orderTotal', 'tags', 'currencyCode', 'channelKey'],
      }
    },
      abortController.signal
    ))
    .map(r => ({
      type: 'order',
      data: r,
      onClick: () => {

        let pane: Pane | undefined;

        if (workspace.state.activePaneId) {
          pane = workspace.state.panes.find(r => r.paneId === workspace.state.activePaneId);
        }
        if (!pane) {
          pane = workspace.state.panes[0];
        }

        if (!pane) { return; }
        
        workspace.openTab(
          pane.paneId,
          <TabView>{
            type: 'view',
            tabId: v7(),
            path: legacyMode ? '/legacy-order' : '/order',
            query: {
              id: r.orderId,
            },
            state: {},
            count: 0,
          }
        )
      }
    }) as SearchResultOrder);

    results.splice(results.length - 1, 0, ...response);
  }
  catch (err: any) {
    if (err.name !== 'AbortError') {
      processes.error({ message: 'Search failed: ' + err });
    }
  }
  finally
  {
    abortController = undefined;
    isLoading.value = false;
  }
}

function select(idx: number) {
  selected.value = idx;
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    selected.value = -1;
    phrase.value = '';
    results.splice(0, results.length);
    return;
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    e.stopPropagation();
    selected.value += 1;
  }
  else if (e.key === 'ArrowUp') {
    e.preventDefault();
    e.stopPropagation();
    selected.value -= 1;
  }
  if (selected.value < -1) {
    selected.value = -1;
  }
  if (selected.value > results.length - 1) {
    selected.value = results.length - 1;
  }
  
  if (e.key === 'Enter' && results.length > 0) {
    e.preventDefault();
    e.stopPropagation();
    
    if (selected.value < 0) {
      open(0);
    }
    
    if (selected.value >= 0) {
      open(selected.value);
    }
  }
}

function open(idx: number) {
  if (idx < -1 || idx > results.length - 1) {
    return;
  }
  
  results[idx].onClick();
  closeResults();
}

</script>

<template>
  <div
    class="quick-search"
    @click.stop
    ref="containerElem"
  >
    <TextInput
      class="quick-search__input"
      :placeholder="t('workspace.quickSearchPlaceholder')"
      v-model="phrase"
      ref="textInput"
      @focus="hasFocus = true"
      @keydown="onKeydown"
    />
    <Spinner
      class="quick-search__spinner"
      :class="{
        'quick-search__spinner--loading': isLoading
      }"
    />

    <Teleport to="body">
    <div
      class="result-list neat-scroll"
      ref="resultsElem"
      :style="resultsStyle"
    >
      <div
        class="no-hits p"
        v-if="phrase.length >= 3 && !isLoading && results.length === 0"
      >
        {{t('common.noHits')}}
      </div>
      
      <div
        v-for="(result, idx) in results"
        :key="idx"
        class="result-item"
        :class="{
          'result-item--selected': selected === idx
        }"
        @click="open(idx)"
      >
        <div class="result-type">
          {{result.type}}
        </div>
        
        <div v-if="result.type === 'query'" class="result-value">
          {{ result.data.name }}
        </div>
        <ResultOrder v-else-if="result.type === 'order'" class="result-value" :result="result"/>
      </div>
    </div>
    </Teleport>
  </div>
</template>

<style scoped>

.quick-search {
  position: relative;
}

:deep(.quick-search__input) {
  padding-right: 32px;
  
}

.quick-search__spinner {
  position: absolute;
  right: 6px;
  top: 6px;
  height: 20px;
  width: 20px;
  opacity: 0;
  transition: opacity 200ms;
}

.quick-search__spinner--loading {
  opacity: 1;
}

.no-hits {
  grid-column: 1 / -1;
  white-space: nowrap;
}

.result-list {
  border: 1px solid hsl(var(--color-60));
  background-color: hsl(var(--color-80));
  border-radius: 4px;
  padding: 0;
  margin-top: 0;
  margin-left: 0;
  margin-right: 0;
  outline: none;
  user-select: none;
  box-shadow: 1px 1px 6px #0000004d;
  
  position: absolute;

  display: grid;
  grid-template-columns: min-content auto;
  grid-auto-rows: 27px;
  
  overflow-x: hidden;
  
  box-sizing: content-box;
  
  max-height: 0;
  transition: max-height 100ms, border 100ms;
  
  z-index: 100;
}

.result-item {
  display: contents;
  cursor: pointer;
}

.result-type {
  border-right: 1px solid hsl(var(--color-70));
}

.result-type,
.result-value {
  padding: 5px;
  border-bottom: 1px solid hsl(var(--color-70));
}

.result-item:last-child .result-type,
.result-item:last-child .result-value {
  border-bottom: none;
}

.result-item:hover .result-type,
.result-item:hover .result-value {
  background-color: hsl(var(--color-70));;
}

.result-item--selected .result-type,
.result-item--selected .result-value {
  background-color: hsl(var(--color-primary));;
}

</style>